import {
  Text,
  Box,
  useCheckboxGroup,
  HStack,
  Stack,
  FormControl,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import debounce from 'lodash/debounce';

import { CustomBadgeCheckbox } from 'components/atoms/form/customBadgeCheckbox';
import {
  CompanyLocationEnum,
  useSearchCitiesLazyQuery,
  WorkModeEnum,
} from 'lib/generated/graphql';
import { SalaryRange, SalarySuffixTypes } from 'components/atoms/salaryRange';
import { RangeSlider } from 'components/molecules/form/rangeSlider';
import { useJobFilters } from 'lib/repositories/jobsShowcaseFilters';
import { salaryDefaults } from 'lib/constants/salary';

import { MobileFilterCard } from '../card';
import { AsyncSelect, SelectOption } from '../../asyncSelect';

export interface AdvancedProps {
  handleSearchJobs(): void;
  defaultSalaryValues: Array<number>;
}

export const Advanced = ({
  handleSearchJobs,
  defaultSalaryValues,
}: AdvancedProps) => {
  const { filters, setType, setCity, setWorkMode, setFilters } =
    useJobFilters();

  const [getCities] = useSearchCitiesLazyQuery();

  const loadCityOptions = debounce(
    (value: string, callBack: (options: SelectOption[]) => void) =>
      getCities({ variables: { textSearch: value } }).then((response) =>
        callBack(
          response.data?.searchCities.map((tech) => ({
            text: tech.name,
            value: tech.id,
          })) || []
        )
      ),
    500
  );

  const { getCheckboxProps: getCheckboxWorkModeProps } = useCheckboxGroup({
    defaultValue: filters.workMode || [],
    value: filters.workMode,
    onChange: (values) => {
      setWorkMode(values.map((item) => item as WorkModeEnum));
    },
  });

  const { getCheckboxProps: getCheckboxTypeProps } = useCheckboxGroup({
    defaultValue: filters.type || [],
    value: filters.type,
    onChange: (values) => {
      setType(values.map((item) => item as string));
    },
  });

  const [salaryRangeValue, setSalaryRangeValue] = useState<Array<number>>([
    filters.minSalary || defaultSalaryValues[0],
    filters.maxSalary || defaultSalaryValues[1],
  ]);

  const onValueChangeEnd = ([min, max]: number[]) => {
    setFilters({ ...filters, maxSalary: max, minSalary: min });
  };

  const onValueChange = (value: number[]) => {
    setSalaryRangeValue(value);
  };

  const resetFilter = () => {
    setFilters({
      ...filters,
      companyLocation: [],
      minSalary: defaultSalaryValues[0],
      maxSalary: defaultSalaryValues[1],
      seniority: [],
      workMode: [],
      type: [],
      city: null,
    });
    setSalaryRangeValue([defaultSalaryValues[0], defaultSalaryValues[1]]);
  };

  const salaryFiltered =
    (filters.minSalary !== defaultSalaryValues[0] &&
      filters.minSalary !== undefined) ||
    (filters.maxSalary !== defaultSalaryValues[1] &&
      filters.maxSalary !== undefined);

  const typeFiltered = filters.type !== undefined && filters.type.length > 0;
  const workModeFiltered =
    filters.workMode !== undefined && filters.workMode.length > 0;
  const seniorityFiltered =
    filters.seniority !== undefined && filters.seniority.length > 0;
  const cityFiltered = filters.city !== undefined && filters.city !== null;

  useEffect(() => {
    setSalaryRangeValue([
      filters.minSalary || defaultSalaryValues[0],
      filters.maxSalary || defaultSalaryValues[1],
    ]);
  }, [defaultSalaryValues, filters.minSalary, filters.maxSalary]);

  return (
    <MobileFilterCard
      showTooltip={false}
      showResetFilter
      resetFilter={resetFilter}
      label='Filtros Avançados'
      title='Filtros Avançados'
      handleSearchJobs={handleSearchJobs}
      isActive={
        salaryFiltered ||
        typeFiltered ||
        workModeFiltered ||
        seniorityFiltered ||
        cityFiltered
      }
    >
      <Stack spacing={6}>
        <FormControl>
          <Text fontWeight={600} mb={2}>
            Tipo de Vaga
          </Text>

          <HStack gap={2}>
            <CustomBadgeCheckbox
              label='Remota'
              {...getCheckboxTypeProps({ value: 'remote' })}
            />
            <CustomBadgeCheckbox
              label='Presencial'
              {...getCheckboxTypeProps({ value: 'no_remote' })}
            />
          </HStack>
        </FormControl>

        {JSON.stringify(filters.companyLocation) !==
          JSON.stringify([CompanyLocationEnum.Us]) && (
          <FormControl>
            <Text fontWeight={600} mb={2}>
              Cidade da empresa
            </Text>

            <AsyncSelect
              label='Cidade da empresa'
              loadOptions={debounce(loadCityOptions, 1000)}
              name='order'
              placeholder='Digite e selecione uma opção'
              selectedOptionStyle='check'
              value={filters.city}
              onChange={(newValue) => setCity(newValue as SelectOption)}
              isMulti={false}
              isClearable
              openMenuOnClick={false}
              defaultOptions
            />
          </FormControl>
        )}

        <FormControl>
          <Text fontWeight={600} mb={2}>
            Regime de Trabalho
          </Text>

          {JSON.stringify(filters.companyLocation) !==
            JSON.stringify([CompanyLocationEnum.Us]) && (
            <HStack gap={2}>
              <CustomBadgeCheckbox
                label='CLT'
                {...getCheckboxWorkModeProps({ value: WorkModeEnum.Clt })}
              />
              <CustomBadgeCheckbox
                label='PJ ou Cooperativa'
                {...getCheckboxWorkModeProps({ value: WorkModeEnum.Pj })}
              />
            </HStack>
          )}

          {JSON.stringify(filters.companyLocation) ===
            JSON.stringify([CompanyLocationEnum.Us]) && (
            <HStack gap={2}>
              <CustomBadgeCheckbox
                label='PJ ou Cooperativa'
                {...getCheckboxWorkModeProps({ value: WorkModeEnum.Pj })}
              />
            </HStack>
          )}
        </FormControl>

        <FormControl>
          <Text fontWeight={600}>Faixa de remuneração mensal</Text>

          <SalaryRange
            minSalary={salaryRangeValue[0]}
            maxSalary={salaryRangeValue[1]}
            currency='R$'
            suffix={SalarySuffixTypes.None}
          />

          <Box px={3}>
            <RangeSlider
              min={defaultSalaryValues[0] || salaryDefaults.min}
              max={defaultSalaryValues[1] || salaryDefaults.max}
              onChangeEnd={onValueChangeEnd}
              onChange={onValueChange}
              value={salaryRangeValue}
              defaultValue={[
                filters.minSalary || defaultSalaryValues[0],
                filters.maxSalary || defaultSalaryValues[1],
              ]}
              mt={2}
            />
          </Box>
        </FormControl>
      </Stack>
    </MobileFilterCard>
  );
};
